'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EmojiItem } from '@tiptap/extension-emoji';
import { Card, CardBody, CardHeader } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/card';
import { Button, ButtonGroup } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/button';
import { Input } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/input';
import { getFilteredEmojis } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/emoji-menu/emoji-menu-utils';
import { EmojiMenuItem } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/emoji-menu';
import { useMenuNavigation } from '@examdojo/core/tiptap-react-ui-components/hooks/use-menu-navigation';
import {
  type IconPickerTab,
  ICON_PICKER_TABS,
  ICON_PICKER_TAB_LABELS,
  CURATED_FA_ICONS,
  CUSTOM_IMAGE_ICONS,
  ALL_CUSTOM_ICONS,
  FA_ICON_COLORS,
  type FaIconColor,
  type CustomIconEntry,
} from './icon-picker-constants';

// --- Selection types ---

export interface IconPickerEmojiSelection {
  type: 'emoji';
  emoji: EmojiItem;
}

export interface IconPickerFaSelection {
  type: 'fa';
  name: string;
  color: string;
  showBackground?: boolean;
}

export interface IconPickerImgSelection {
  type: 'img';
  name: string;
  src: string;
}

export type IconPickerSelection = IconPickerEmojiSelection | IconPickerFaSelection | IconPickerImgSelection;

// --- Tab: Custom Icons ---

interface IconsTabProps {
  searchQuery: string;
  onSelect: (selection: IconPickerImgSelection) => void;
  activeValue?: string;
}

function IconsTab({ searchQuery, onSelect, activeValue }: IconsTabProps) {
  const filteredCategories = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) {
      return CUSTOM_IMAGE_ICONS;
    }
    return CUSTOM_IMAGE_ICONS.map((category) => ({
      ...category,
      icons: category.icons.filter((entry) => entry.label.toLowerCase().includes(query)),
    })).filter((category) => category.icons.length > 0);
  }, [searchQuery]);

  const allIcons = useMemo(
    () => (searchQuery.trim() ? filteredCategories.flatMap((c) => c.icons) : ALL_CUSTOM_ICONS),
    [filteredCategories, searchQuery],
  );

  if (allIcons.length === 0) {
    return <div className="icon-picker-empty">No icons available</div>;
  }

  return (
    <div className="icon-picker-grid-container">
      {filteredCategories.map((category) => (
        <div key={category.label} className="icon-picker-category">
          <div className="icon-picker-category-label">{category.label}</div>
          <div className="icon-picker-grid">
            {category.icons.map((entry) => (
              <button
                key={entry.src}
                type="button"
                className={`icon-picker-grid-item ${activeValue === entry.src ? 'active' : ''}`}
                title={entry.label}
                onClick={() => onSelect({ type: 'img', name: entry.label, src: entry.src })}
              >
                <img src={entry.src} alt={entry.label} className="icon-picker-img" />
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Tab: FontAwesome ---

interface FaTabProps {
  searchQuery: string;
  onSelect: (selection: IconPickerFaSelection) => void;
  onSettingsChange?: (selection: IconPickerFaSelection) => void;
  initialColor?: string;
  initialShowBackground?: boolean;
  activeIcon?: string;
}

function FaTab({ searchQuery, onSelect, onSettingsChange, initialColor, initialShowBackground = true, activeIcon }: FaTabProps) {
  const [selectedColor, setSelectedColor] = useState<FaIconColor>(() => {
    if (initialColor) {
      return FA_ICON_COLORS.find((c) => c.value === initialColor) || FA_ICON_COLORS[0];
    }
    return FA_ICON_COLORS[0];
  });
  const [showBackground, setShowBackground] = useState(initialShowBackground);

  const filteredCategories = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) {
      return CURATED_FA_ICONS;
    }
    return CURATED_FA_ICONS.map((category) => ({
      ...category,
      icons: category.icons.filter((iconName) => iconName.toLowerCase().includes(query)),
    })).filter((category) => category.icons.length > 0);
  }, [searchQuery]);

  const hasResults = filteredCategories.some((c) => c.icons.length > 0);

  return (
    <div className="icon-picker-fa-container">
      {/* Color picker */}
      <div className="icon-picker-color-row">
        <span className="icon-picker-color-label">Color</span>
        <div className="icon-picker-color-swatches">
          {FA_ICON_COLORS.map((colorObj) => (
            <button
              key={colorObj.value}
              type="button"
              className={`icon-picker-color-swatch ${selectedColor.value === colorObj.value ? 'active' : ''}`}
              style={{
                backgroundColor: colorObj.value === 'currentColor' ? 'var(--tt-gray-light-a-500)' : colorObj.value,
              }}
              title={colorObj.label}
              onClick={() => {
                setSelectedColor(colorObj);
                if (activeIcon && onSettingsChange) {
                  onSettingsChange({ type: 'fa', name: activeIcon, color: colorObj.value, showBackground });
                }
              }}
            />
          ))}
        </div>
      </div>

      {/* Background toggle */}
      <div className="icon-picker-bg-row">
        <label className="icon-picker-bg-toggle">
          <input
            type="checkbox"
            checked={showBackground}
            onChange={(e) => {
              const newVal = e.target.checked;
              setShowBackground(newVal);
              if (activeIcon && onSettingsChange) {
                onSettingsChange({ type: 'fa', name: activeIcon, color: selectedColor.value, showBackground: newVal });
              }
            }}
          />
          <span className="icon-picker-bg-label">Show background</span>
        </label>
      </div>

      {/* Icons grid */}
      {!hasResults ? (
        <div className="icon-picker-empty">No icons found</div>
      ) : (
        <div className="icon-picker-grid-container">
          {filteredCategories.map((category) => (
            <div key={category.label} className="icon-picker-category">
              <div className="icon-picker-category-label">{category.label}</div>
              <div className="icon-picker-grid">
                {category.icons.map((iconName) => (
                  <button
                    key={iconName}
                    type="button"
                    className={`icon-picker-grid-item ${activeIcon === iconName ? 'active' : ''}`}
                    title={iconName}
                    onClick={() => onSelect({ type: 'fa', name: iconName, color: selectedColor.value, showBackground })}
                  >
                    <i
                      className={`fas fa-${iconName}`}
                      style={{
                        color: selectedColor.value,
                        fontSize: '1.125rem',
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Tab: Emojis ---

interface EmojisTabProps {
  emojis: EmojiItem[];
  searchQuery: string;
  onSelect: (selection: IconPickerEmojiSelection) => void;
  activeEmoji?: string;
}

function EmojisTab({ emojis, searchQuery, onSelect, activeEmoji }: EmojisTabProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const filteredEmojis = useMemo(() => getFilteredEmojis({ query: searchQuery, emojis }), [emojis, searchQuery]);

  const { selectedIndex } = useMenuNavigation({
    containerRef,
    query: searchQuery,
    items: filteredEmojis,
    onSelect: (emoji) => onSelect({ type: 'emoji', emoji }),
  });

  if (filteredEmojis.length === 0) {
    return <div className="icon-picker-empty">No emojis found</div>;
  }

  return (
    <div ref={containerRef} className="icon-picker-grid-container emoji-menu-list">
      <ButtonGroup>
        {filteredEmojis.map((emoji, index) => (
          <span key={emoji.name} className={activeEmoji === emoji.emoji ? 'icon-picker-emoji-active' : ''}>
            <EmojiMenuItem
              emoji={emoji}
              index={index}
              isSelected={index === selectedIndex}
              onSelect={() => onSelect({ type: 'emoji', emoji })}
              selector=".emoji-menu-list"
            />
          </span>
        ))}
      </ButtonGroup>
    </div>
  );
}

// --- Main IconPicker ---

export interface IconPickerProps {
  emojis: EmojiItem[];
  onSelect: (selection: IconPickerSelection) => void;
  /** Fires when color/background settings change for the already-active icon. */
  onSettingsChange?: (selection: IconPickerSelection) => void;
  onClose?: () => void;
  initialTab?: IconPickerTab;
  showSearch?: boolean;
  initialFaColor?: string;
  initialShowBackground?: boolean;
  /** Currently active icon identifier — FA icon name, image src, or emoji character */
  activeIconValue?: string;
}

export function IconPicker({
  emojis,
  onSelect,
  onSettingsChange,
  onClose,
  initialTab = 'fontawesome',
  showSearch = true,
  initialFaColor,
  initialShowBackground = true,
  activeIconValue,
}: IconPickerProps) {
  const [activeTab, setActiveTab] = useState<IconPickerTab>(initialTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentActiveIcon, setCurrentActiveIcon] = useState(activeIconValue);
  const searchRef = useRef<HTMLInputElement>(null);

  const handleTabChange = useCallback((tab: IconPickerTab) => {
    setActiveTab(tab);
    setSearchQuery('');
  }, []);

  const handleSelect = useCallback(
    (selection: IconPickerSelection) => {
      switch (selection.type) {
        case 'fa':
          setCurrentActiveIcon(selection.name);
          break;
        case 'img':
          setCurrentActiveIcon(selection.src);
          break;
        case 'emoji':
          setCurrentActiveIcon(selection.emoji.emoji);
          break;
      }
      onSelect(selection);
    },
    [onSelect],
  );

  // Focus search input when tab changes
  useEffect(() => {
    if (showSearch) {
      searchRef.current?.focus();
    }
  }, [activeTab, showSearch]);

  return (
    <Card className="icon-picker-card">
      {/* Tab bar */}
      <div className="icon-picker-tabs">
        {ICON_PICKER_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            className={`icon-picker-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => handleTabChange(tab)}
          >
            {ICON_PICKER_TAB_LABELS[tab]}
          </button>
        ))}
      </div>

      {/* Search bar */}
      {showSearch && (
        <CardHeader>
          <Input
            ref={searchRef}
            type="text"
            placeholder={`Search ${ICON_PICKER_TAB_LABELS[activeTab].toLowerCase()}...`}
            value={searchQuery}
            autoFocus
            onChange={(e) => setSearchQuery(e.target.value)}
            className="icon-picker-search-input"
          />
        </CardHeader>
      )}

      {/* Tab content */}
      <CardBody className="icon-picker-body">
        {activeTab === 'icons' && (
          <IconsTab searchQuery={searchQuery} onSelect={handleSelect} activeValue={currentActiveIcon} />
        )}
        {activeTab === 'fontawesome' && (
          <FaTab
            searchQuery={searchQuery}
            onSelect={handleSelect}
            onSettingsChange={onSettingsChange}
            initialColor={initialFaColor}
            initialShowBackground={initialShowBackground}
            activeIcon={currentActiveIcon}
          />
        )}
        {activeTab === 'emojis' && (
          <EmojisTab emojis={emojis} searchQuery={searchQuery} onSelect={handleSelect} activeEmoji={currentActiveIcon} />
        )}
      </CardBody>
    </Card>
  );
}
